'use client';

import { Minus, Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Button } from '@//components/Button';
import { useLegacyTrackChargingCostDetailsClick } from '@//data/tracking/useLegacyTrackChargingCostDetailsClick';
import styles from './WidgetDetails.module.scss';

interface WidgetDetailsProps {
  id: string;
  children: React.ReactNode;
  distance?: string;
  chargingCost?: number;
}

export const WidgetDetails: React.FC<WidgetDetailsProps> = ({
  id,
  children,
  distance,
  chargingCost,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  const { trackLegacyChargingCostDetailsClick } = useLegacyTrackChargingCostDetailsClick();
  const t = useTranslations('other.common.actions');

  const toggleDetails = () => {
    setShowDetails((prev) => !prev);
    if (!showDetails) {
      // Track when showing details
      if (distance) {
        trackLegacyChargingCostDetailsClick({
          information: {
            context: 'modal',
            disance: distance,
            chargingCost,
          },
        });
      }

      setTimeout(() => {
        detailsRef.current?.focus();
      }, 0);
    }
  };

  return (
    <>
      {showDetails && (
        <div id={id} className={styles.details} tabIndex={-1} ref={detailsRef}>
          {children}
        </div>
      )}
      <Button
        variant="outline"
        size="md"
        className={styles.button}
        onClick={toggleDetails}
        title={showDetails ? t('hideDetails') : t('showDetails')}
        aria-expanded={showDetails}
        aria-controls={id}
      >
        {showDetails ? (
          <>
            {t('hideDetails')}
            <Minus aria-hidden="true" />
          </>
        ) : (
          <>
            {t('showDetails')}
            <Plus aria-hidden="true" />
          </>
        )}
      </Button>
    </>
  );
};
